/* ==========================================================================
   Admin Pricing Page
   PORTAL-204: Display-only pricing. Pharmacy admins manage named pricing
   lists and choose which list each provider sees; providers never pay
   through the portal.

   Editing lists needs the formulary permission (Admin / Manager). Viewing
   and assigning lists to providers is open to every pharmacy user.
   ========================================================================== */

import { useCallback, useEffect, useState } from 'react'
import { Plus, RefreshCw } from 'lucide-react'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Alert } from '@/components/ui/Alert'
import { AdminTableSkeleton } from '@/components/skeletons'
import {
  DeletePricingListModal,
  EditPricingListModal,
  NewPricingListModal,
  type NewPricingListPreset,
} from '@/components/admin/pricing/PricingListModals'
import { PricingProvidersTab } from '@/components/admin/pricing/PricingProvidersTab'
import { useAdminAuth, useAdminPermissions } from '@/hooks/useAdminAuth'
import { listPricingLists, listProviders } from '@/api/admin'
import { ApiClientError } from '@/api/client'
import type { PricingList, PricingListListResponse, ProviderResponse } from '@/api/types'
import { describePricingRule, providerCountLabel } from '@/utils/pricing'

type Tab = 'lists' | 'providers'

function formatUpdated(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

/* --------------------------------------------------------------------------
   Main Component
   -------------------------------------------------------------------------- */

export function Pricing() {
  const { pharmacy } = useAdminAuth()
  const { canManage } = useAdminPermissions()
  const slug = pharmacy?.portal_slug || ''

  const [tab, setTab] = useState<Tab>('lists')
  const [data, setData] = useState<PricingListListResponse | null>(null)
  const [providers, setProviders] = useState<ProviderResponse[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Modals
  const [newPreset, setNewPreset] = useState<NewPricingListPreset | null>(null)
  const [newOpen, setNewOpen] = useState(false)
  const [editing, setEditing] = useState<PricingList | null>(null)
  const [deleting, setDeleting] = useState<PricingList | null>(null)

  const fetchData = useCallback(async () => {
    if (!slug) return
    setError(null)
    try {
      const [listsResponse, providersResponse] = await Promise.all([
        listPricingLists(slug),
        listProviders(slug),
      ])
      setData(listsResponse)
      setProviders(providersResponse.providers)
    } catch (err) {
      setError(
        err instanceof ApiClientError
          ? err.message
          : 'Failed to load pricing. Please try again.'
      )
    } finally {
      setLoading(false)
    }
  }, [slug])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const lists = data?.lists ?? []

  function openNew(preset: NewPricingListPreset | null) {
    setNewPreset(preset)
    setNewOpen(true)
  }

  const tabs: { key: Tab; label: string; count: number }[] = [
    { key: 'lists', label: 'Pricing Lists', count: lists.length + 1 },
    { key: 'providers', label: 'Provider Assignments', count: providers.length },
  ]

  return (
    <div className="p-6 sm:p-8">
      <div className="max-w-6xl">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-6">
          <div>
            <h1 className="font-serif text-2xl sm:text-3xl text-charcoal flex flex-wrap items-center gap-3">
              Pricing
              <span className="font-sans text-xs font-medium rounded-full bg-soft-lavender text-deep-purple px-3 py-1">
                Display only — shows providers their price. Payments aren’t made through the portal.
              </span>
            </h1>
            <p className="text-charcoal/70 mt-1 max-w-3xl">
              Set a default price per unit for each product, then override it for specific
              providers with pricing lists. Providers see the price per unit and an estimated
              total for the quantity they prescribe.
            </p>
          </div>
          {canManage && (
            <Button variant="primary" onClick={() => openNew(null)} className="shrink-0">
              <Plus className="w-4 h-4 mr-2" />
              New Pricing List
            </Button>
          )}
        </div>

        {error && (
          <Alert variant="error" className="mb-6">
            {error}
            <Button variant="ghost" size="sm" onClick={fetchData} className="ml-4">
              <RefreshCw className="w-4 h-4 mr-1" />
              Retry
            </Button>
          </Alert>
        )}

        {/* Tabs */}
        <div className="flex gap-6 border-b border-gray-200 mb-6" role="tablist">
          {tabs.map((t) => (
            <button
              key={t.key}
              role="tab"
              aria-selected={tab === t.key}
              onClick={() => setTab(t.key)}
              className={`-mb-px pb-3 text-sm font-medium border-b-2 transition-colors ${
                tab === t.key
                  ? 'border-deep-purple text-deep-purple'
                  : 'border-transparent text-gray-500 hover:text-charcoal'
              }`}
            >
              {t.label}
              <span className="ml-1 text-gray-400">({t.count})</span>
            </button>
          ))}
        </div>

        {loading ? (
          <Card>
            <AdminTableSkeleton columns={5} rows={4} />
          </Card>
        ) : tab === 'lists' ? (
          data && (
            <PricingListsTable
              data={data}
              canManage={canManage}
              onEdit={setEditing}
              onDuplicate={(l) =>
                openNew({ name: `${l.name} (copy)`, startFrom: 'duplicate', sourceListId: l.id })
              }
              onDelete={setDeleting}
            />
          )
        ) : (
          <PricingProvidersTab
            slug={slug}
            providers={providers}
            lists={lists}
            onAssigned={fetchData}
          />
        )}
      </div>

      <NewPricingListModal
        open={newOpen}
        preset={newPreset}
        slug={slug}
        lists={lists}
        productTotal={data?.product_total ?? 0}
        onClose={() => setNewOpen(false)}
        onDone={fetchData}
      />
      <EditPricingListModal
        list={editing}
        slug={slug}
        lists={lists}
        onClose={() => setEditing(null)}
        onDone={fetchData}
      />
      <DeletePricingListModal
        list={deleting}
        slug={slug}
        lists={lists}
        onClose={() => setDeleting(null)}
        onDone={fetchData}
      />
    </div>
  )
}

/* --------------------------------------------------------------------------
   Pricing Lists table: the "Default price" base row, then one row per list
   -------------------------------------------------------------------------- */

interface PricingListsTableProps {
  data: PricingListListResponse
  canManage: boolean
  onEdit: (list: PricingList) => void
  onDuplicate: (list: PricingList) => void
  onDelete: (list: PricingList) => void
}

interface Row {
  key: string
  name: string
  description: string | null
  rule: string
  priced: string
  providers: string
  updated: string
  list: PricingList | null // null for the "Default price" base row
}

function PricingListsTable({ data, canManage, onEdit, onDuplicate, onDelete }: PricingListsTableProps) {
  const rows: Row[] = [
    {
      key: 'default',
      name: 'Default price',
      description: 'The price field on every product',
      rule: 'Base — no overrides',
      priced: `${data.default_priced_count} / ${data.product_total}`,
      providers: providerCountLabel(data.default_provider_count),
      updated: 'Live',
      list: null,
    },
    ...data.lists.map((l) => ({
      key: l.id,
      name: l.name,
      description: l.description,
      rule: describePricingRule(l),
      priced: `${l.entry_count} / ${data.product_total}`,
      providers: providerCountLabel(l.provider_count),
      updated: formatUpdated(l.updated_at),
      list: l,
    })),
  ]

  function actions(list: PricingList) {
    if (!canManage) return null
    return (
      <div className="flex gap-3 mt-1 text-xs font-semibold">
        <button onClick={() => onEdit(list)} className="text-deep-purple hover:underline">
          Rename
        </button>
        <button onClick={() => onDuplicate(list)} className="text-deep-purple hover:underline">
          Duplicate
        </button>
        <button onClick={() => onDelete(list)} className="text-error hover:underline">
          Delete
        </button>
      </div>
    )
  }

  function nameCell(row: Row) {
    return (
      <>
        <div className="font-medium text-charcoal flex items-center gap-2">
          {row.name}
          {!row.list && (
            <span className="text-[10px] uppercase tracking-wide font-semibold rounded bg-gray-100 text-gray-600 px-1.5 py-0.5">
              Base
            </span>
          )}
        </div>
        {row.description && <div className="text-sm text-gray-500">{row.description}</div>}
        {row.list && actions(row.list)}
      </>
    )
  }

  return (
    <Card className="overflow-visible">
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-charcoal">Pricing lists</h2>
        <p className="text-sm text-gray-500">
          A list is a named pack of prices. Assign it to any number of providers.
        </p>
      </div>

      {/* Desktop table */}
      <table className="hidden md:table w-full">
        <thead>
          <tr className="border-b border-gray-200">
            {['List', 'Rule', 'Priced', 'Providers', 'Updated'].map((h) => (
              <th key={h} className="text-left text-sm font-medium text-gray-600 pb-3 pr-4">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key} className="border-b border-gray-100 last:border-b-0 align-top">
              <td className="py-4 pr-4">{nameCell(row)}</td>
              <td className="py-4 pr-4 text-sm text-gray-700">{row.rule}</td>
              <td className="py-4 pr-4 text-sm text-gray-700 whitespace-nowrap">{row.priced}</td>
              <td className="py-4 pr-4 text-sm text-gray-700 whitespace-nowrap">{row.providers}</td>
              <td className="py-4 text-sm text-gray-500 whitespace-nowrap">{row.updated}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Mobile cards */}
      <div className="md:hidden space-y-3">
        {rows.map((row) => (
          <div key={row.key} className="border border-gray-200 rounded-lg p-4 space-y-2">
            {nameCell(row)}
            <div className="text-sm text-gray-700">{row.rule}</div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
              <span>Priced {row.priced}</span>
              <span>{row.providers}</span>
              <span>Updated {row.updated}</span>
            </div>
          </div>
        ))}
      </div>

      {data.lists.length === 0 && (
        <p className="mt-4 text-sm text-gray-500">
          No pricing lists yet. Every provider sees each product’s default price.
          {canManage && ' Create a list to give specific providers different prices.'}
        </p>
      )}
    </Card>
  )
}

export default Pricing
